import { normalize_text as normalize_form_text } from '@/services/onboarding_form_helpers';
import {
  build_manual_verification_checks,
  build_verification_checks,
  fetch_verification_statuses,
  hydrate_case_detail,
  resolve_workflow_display_stage
} from '@/services/onboarding_journey_service';
import { title_case } from '@/services/onboarding_data_helpers';

export const normalize_stage = (value) => String(value || '').trim().toLowerCase().replace(/[_-]+/g, ' ');

export const normalize_label = (value) => normalize_form_text(String(value || '').replace(/[_-]+/g, ' '));

const SERVICE_FIELD_LABEL_RULES = [
  {
    matches: (service_name) => service_name.includes('identity') || service_name.includes('id verification') || service_name.includes('idv'),
    labels: ['id number', 'identity number', 'id no', 'id']
  },
  {
    matches: (service_name) => service_name.includes('kyc'),
    labels: ['id number', 'identity number', 'surname', 'last name']
  },
  {
    matches: (service_name) => service_name.includes('aml'),
    labels: ['id number', 'identity number', 'first name', 'surname', 'last name']
  },
  {
    matches: (service_name) => service_name.includes('credit'),
    labels: ['consumer id number', 'id number', 'identity number']
  },
  {
    matches: (service_name) => service_name.includes('cipc'),
    labels: ['registration number', 'company registration', 'unique company identifier', 'company type code', 'year']
  }
];

export const get_service_field_label_candidates = (service = {}) => {
  const service_name = normalize_label(service?.name || service?.service_name || service?.title || '');
  return SERVICE_FIELD_LABEL_RULES.find((rule) => rule.matches(service_name))?.labels || [];
};

export const get_case_match_values = (case_item = {}) => [
  Number(case_item?.application_id || 0),
  Number(case_item?.id || 0),
  Number(case_item?.entity_profile_id || 0)
];

function get_case_gate_status(case_item = {}) {
  return String(first_case_gate_status_value(case_item) || 'In progress').trim();
}

function first_case_gate_status_value(case_item = {}) {
  return [
    case_item?.current_workflow_status,
    case_item?.workflow_display_stage,
    case_item?.stage,
    case_item?.detail,
    case_item?.application_status,
    case_item?.status?.status,
    case_item?.status?.name,
    case_item?.status_name,
    case_item?.status
  ].find((value) => value !== undefined && value !== null && value !== '');
}

const CASE_GATE_TONE_RULES = [
  { tone: 'danger', words: ['rejected', 'blocked', 'failed'] },
  { tone: 'success', words: ['approved', 'complete', 'completed', 'activated'] },
  { tone: 'warning', words: ['verification', 'documents', 'review', 'pending', 'submitted'] }
];

function get_case_gate_tone(status = '') {
  const normalized = normalize_stage(status);
  const matched_rule = CASE_GATE_TONE_RULES.find((rule) => rule.words.some((value) => normalized.includes(value)));
  return matched_rule?.tone || 'secondary';
}

export function create_case_row(row_key, case_item, check_item) {
  const gate_status = get_case_gate_status(case_item);
  return build_case_row_payload(row_key, case_item, check_item, gate_status);
}

function build_case_row_payload(row_key, case_item, check_item, gate_status) {
  return {
    id: row_key,
    case_item,
    case_name: get_case_row_name(case_item, check_item),
    type: get_case_row_type(case_item),
    reviewer: get_case_row_reviewer(case_item),
    verification_count: 0,
    open_count: 0,
    blocked_count: 0,
    status: gate_status,
    status_tone: get_case_gate_tone(gate_status),
    impact: 'Ready to run'
  };
}

function get_case_row_name(case_item = {}, check_item = {}) {
  return first_non_empty_value([case_item?.name, check_item?.case_name, 'Onboarded Entity']);
}

function get_case_row_type(case_item = {}) {
  return first_non_empty_value([case_item?.journey, case_item?.journey_tone, 'Onboarding']);
}

function get_case_row_reviewer(case_item = {}) {
  return first_non_empty_value([case_item?.owner, 'Unassigned']);
}

function first_non_empty_value(values = []) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

const increment_row_counts = (row, check_item) => {
  if (check_item?.is_gate_placeholder) return;
  row.verification_count += 1;
  if (check_item?.status_tone !== 'success') {
    row.open_count += 1;
  }
};

const CHECK_ROW_STATUS_HANDLERS = [
  {
    matches: (row, check_item) => check_item?.status_tone === 'danger',
    apply: (row) => {
      row.blocked_count += 1;
      row.status = 'Blocked';
      row.status_tone = 'danger';
      row.impact = 'Needs remediation';
    }
  },
  {
    matches: (row, check_item) => row.status_tone !== 'danger' && check_item?.status_tone === 'warning',
    apply: (row) => {
      row.status = 'Pending';
      row.status_tone = 'warning';
      row.impact = 'Needs review';
    }
  },
  {
    matches: (row, check_item) => row.status_tone !== 'danger' && row.status_tone !== 'warning' && check_item?.status_tone === 'success',
    apply: (row) => {
      row.status = row.open_count > 0 ? 'Partially complete' : 'Verified';
      row.status_tone = row.open_count > 0 ? 'warning' : 'success';
      row.impact = row.open_count > 0 ? 'More checks remaining' : 'Can proceed';
    }
  }
];

export const apply_check_status_to_row = (row, check_item) => {
  const gate_status = row.status;
  const gate_status_tone = row.status_tone;
  increment_row_counts(row, check_item);
  const handler = CHECK_ROW_STATUS_HANDLERS.find((candidate) => candidate.matches(row, check_item));
  handler?.apply(row);
  row.status = gate_status;
  row.status_tone = gate_status_tone;
};

const build_selected_check_history = (existing_item, item, secondary_item) => {
  const merged_history = [
    ...(Array.isArray(existing_item?.history) ? existing_item.history : []),
    ...(Array.isArray(item?.history) ? item.history : []),
    ...(secondary_item?.raw_log ? [{
      id: secondary_item.id,
      status: secondary_item.status,
      status_tone: secondary_item.status_tone,
      detail: secondary_item.impact,
      last_run: secondary_item.last_run,
      raw_log: secondary_item.raw_log
    }] : [])
  ];

  return merged_history
    .filter((entry, index, entries) => (
      entries.findIndex((candidate) => candidate?.id === entry?.id) === index
    ))
    .sort((left, right) => String(right?.last_run || '').localeCompare(String(left?.last_run || '')));
};

export const get_auto_check_group_key = (item = {}) => {
  if (item?.verification_mode === 'manual') {
    return `manual:${item?.verification_type || item?.id || 'manual'}`;
  }

  const service_id = Number(item?.service_id || 0);
  if (service_id > 0) {
    return `auto-service:${service_id}`;
  }

  return `auto-name:${String(item?.check || '').trim().toLowerCase()}`;
};

export const get_check_sort_value = (item = {}) => {
  const timestamp = Date.parse(item?.raw_status?.timestamp || item?.raw_log?.created_at || '');
  if (!Number.isNaN(timestamp)) {
    return timestamp;
  }

  return item?.last_run === '-' ? 0 : Date.parse(item?.last_run || '') || 0;
};

export const merge_selected_check_group = (grouped_checks, item) => {
  const group_key = get_auto_check_group_key(item);
  const existing_item = grouped_checks.get(group_key);

  if (!existing_item) {
    grouped_checks.set(group_key, {
      ...item,
      history: Array.isArray(item?.history) ? [...item.history] : [],
      grouped_runs: 1
    });
    return;
  }

  const existing_sort = get_check_sort_value(existing_item);
  const incoming_sort = get_check_sort_value(item);
  const primary_item = incoming_sort > existing_sort ? item : existing_item;
  const secondary_item = primary_item === item ? existing_item : item;

  grouped_checks.set(group_key, {
    ...primary_item,
    history: build_selected_check_history(existing_item, item, secondary_item),
    grouped_runs: Number(existing_item?.grouped_runs || 1) + 1
  });
};

export const get_case_entity_type_id = (hydrated_case = {}, case_item = {}) => Number(
  hydrated_case?.entity_type_id
  || hydrated_case?.profile?.entity_type?.id
  || hydrated_case?.profile?.entity_type_id
  || case_item?.entity_type_id
  || 0
);

export const get_bound_services = (verification_binding_data, services = []) => (
  Array.isArray(verification_binding_data?.service_ids) && verification_binding_data.service_ids.length
    ? verification_binding_data.service_ids
      .map((service_id) => services.find((service) => Number(service?.id) === Number(service_id)))
      .filter(Boolean)
    : []
);

export const attach_case_workflow_stage = (hydrated_case, bound_services, verification_binding_data) => {
  const workflow_stage = resolve_workflow_display_stage(hydrated_case);
  const verification_details = {
    auto_verification_services: bound_services,
    auto_verification_entity_config_id: verification_binding_data?.entity_config_id || null,
    manual_verification_bindings: Array.isArray(verification_binding_data?.manual_bindings)
      ? verification_binding_data.manual_bindings
      : []
  };

  return workflow_stage
    ? {
      ...hydrated_case,
      workflow_display_stage: workflow_stage,
      ...verification_details
    }
    : {
      ...hydrated_case,
      ...verification_details
    };
};

export const build_verification_statuses_by_form = async (forms = []) => {
  const verification_status_pairs = await Promise.all(
    forms.map(async (form) => {
      const application_form_id = form?.application_form_id || form?.id;
      const statuses = await fetch_verification_statuses(application_form_id).catch(() => ({}));
      return [application_form_id, statuses];
    })
  );

  return Object.fromEntries(verification_status_pairs);
};

export const get_verification_detail_route_name = (is_manual_page) => (
  is_manual_page ? 'OnboardingManualVerificationCase' : 'OnboardingVerificationCase'
);

export const get_verification_detail_company = (case_item = {}) => (
  case_item?.onboarding_project_name
  || case_item?.profile?.project?.name
  || case_item?.project_name
  || ''
);

export const hydrate_verification_case = async (case_item, forms, documents) => hydrate_case_detail({
  ...case_item,
  forms,
  documents
}).catch(() => ({
  ...case_item,
  forms,
  documents
}));

export const get_verification_binding_fallback = () => ({
  entity_config_id: null,
  service_ids: []
});

export const MANUAL_SECTION_TAB_PRECEDENCE = Object.freeze([
  { id: 'manual-review-checklist', label: '1. Manual Review' }
]);

export const DEFAULT_MANUAL_SECTION_TAB = MANUAL_SECTION_TAB_PRECEDENCE[0].id;

export const resolve_manual_section_tab = (section_id = '') => {
  const tab_id = String(section_id || '').trim();
  const matched_tab = MANUAL_SECTION_TAB_PRECEDENCE.find((tab) => tab.id === tab_id);
  return matched_tab?.id || DEFAULT_MANUAL_SECTION_TAB;
};

export const VERIFICATION_VIEW_STATE = {
  active_filter: 'all',
  is_loading_checks: true,
  is_loading_detail_case: false,
  checks_error: '',
  previous_gate_mode: 'auto',
  selected_case: null,
  configured_gate_ids: [],
  workflow_transition_history: [],
  previous_manual_history_run: null,
  selected_case_summary: null,
  active_summary_detail_tab: 'summary',
  is_summary_detail_fullscreen: false,
  is_loading_summary_detail: false,
  summary_detail_tabs: [
    { id: 'summary', label: 'Summary', icon: 'bi-grid' }
  ],
  summary_rows: [],
  summary_stage_timeline: [],
  summary_case_form_sections: [],
  sign_modal: {
    visible: false,
    agreement_title: 'Supplier agreement',
    signatory_name: '',
    document_id: 'DOC-VERIFICATION-2026'
  },
  manual_review_modal: {
    visible: false,
    is_submitting: false,
    is_saving_field: false,
    item: null,
    run: null,
    form_sections: [],
    field_form: null,
    decision_status: 'pass',
    decision_reason: ''
  },
  document_preview_modal: {
    visible: false,
    title: '',
    url: '',
    type: '',
    detail: null
  },
  manual_workspace: {
    reviewer_comments: '',
    decision_status: 'approve',
    checklist_items: []
  },
  active_auto_section: 'auto-verification-results',
  active_manual_section: DEFAULT_MANUAL_SECTION_TAB,
  active_manual_applicant_section_id: '',
  expanded_manual_applicant_form_ids: [],
  manual_applicant_forms_expansion_initialized: false,
  run_feedback: '',
  run_feedback_tone: 'success',
  running_auto_keys: [],
  running_manual_keys: [],
  bank_statement_poll_timer_id: null,
  bank_statement_poll_run_id: null,
  running_manual_field_keys: [],
  expanded_check_keys: [],
  filters: [
    { label: 'All', count: '0', tone: 'all' },
    { label: 'Open', tone: 'open' },
    { label: 'Mine', tone: 'mine' },
    { label: 'Blocked', tone: 'blocked' }
  ],
  checks: []
};

export const resolve_verification_workspace_mode = (route = {}) => {
  const query_mode = normalize_stage(route?.query?.mode || '');
  if (query_mode === 'field verification') return 'field-verification';

  const meta_mode = normalize_stage(route?.meta?.gateMode || '');
  if (meta_mode === 'manual') return 'manual';

  return 'verification';
};

const is_auto_verification_stage = (item = {}, normalized_stage) => (
  item?.verification_mode !== 'manual'
  && normalized_stage.includes('verif')
  && !normalized_stage.includes('field verif')
  && !normalized_stage.includes('manual verif')
);

export const is_field_verification_stage = (case_item = {}) => {
  const stage_values = [
    case_item?.current_workflow_status,
    case_item?.workflow_display_stage,
    case_item?.stage,
    case_item?.detail,
    case_item?.application_status,
    case_item?.status,
    case_item?.status_name
  ];

  return stage_values.some((value) => {
    const normalized_value = normalize_stage(value);
    return normalized_value.includes('field verif');
  });
};

export const filter_verification_checks = (checks = [], workspace_mode = 'verification', active_filter = 'all') => {
  const mode_filtered_checks = checks.filter((item) => {
    const case_item = item?.case_item || {};
    const normalized_stage = normalize_stage(
      case_item?.current_workflow_status
      || case_item?.workflow_display_stage
      || case_item?.stage
      || case_item?.detail
      || case_item?.application_status
      || case_item?.status
      || case_item?.status_name
    );

    if (workspace_mode === 'manual') {
      return is_manual_verification_stage(case_item);
    }

    if (workspace_mode === 'field-verification') {
      return is_field_verification_stage(case_item);
    }

    return is_auto_verification_stage(item, normalized_stage);
  });

  if (active_filter === 'all') return mode_filtered_checks;
  if (active_filter === 'open') return mode_filtered_checks.filter((item) => item.status_tone !== 'success');
  if (active_filter === 'blocked') return mode_filtered_checks.filter((item) => item.status_tone === 'danger');
  return mode_filtered_checks;
};

export const build_manual_workspace_documents = (selected_case, get_manual_document_status) => {
  const documents = Array.isArray(selected_case?.documents) ? selected_case.documents : [];
  return documents.map((document, index) => build_manual_workspace_document(document, index, selected_case, get_manual_document_status));
};

const get_manual_document_id = (document, index, selected_case) => (
  document?.id || `${selected_case?.application_id || 'manual'}-document-${index}`
);

const get_manual_document_name = (document) => String(
  document?.question || document?.file_name || document?.name || 'Document'
).trim() || 'Document';

const get_manual_document_type = (document) => String(
  document?.section || document?.document_type || document?.file_type || 'Uploaded document'
).trim() || 'Uploaded document';

const get_manual_document_file_url = (document) => String(
  document?.presigned_url || document?.file_url || document?.url || document?.document_url || ''
).trim();

const get_manual_document_file_type = (document) => String(
  document?.content_type || document?.mime_type || document?.file_type || ''
).trim();

const build_manual_workspace_document = (document, index, selected_case, get_manual_document_status) => ({
  id: get_manual_document_id(document, index, selected_case),
  name: get_manual_document_name(document),
  type: get_manual_document_type(document),
  uploaded_on: document?.submitted_on || document?.created_at || document?.updated_at || '',
  file_name: String(document?.file_name || document?.name || '').trim(),
  file_url: get_manual_document_file_url(document),
  file_type: get_manual_document_file_type(document),
  owner_name: selected_case?.name || '',
  status: get_manual_document_status(document)
});

export const get_manual_document_status = (document = {}) => {
  if (document?.is_verified === true) return { label: 'Verified', tone: 'success' };
  const file_url = String(document?.presigned_url || document?.file_url || document?.url || document?.document_url || '').trim();
  return file_url ? { label: 'Uploaded', tone: 'warning' } : { label: 'Missing', tone: 'danger' };
};

export const MANUAL_DECISION_STATUS_MAP = Object.freeze({
  approve: 'pass',
  reject: 'fail',
  request_changes: 'partial'
});

const SAFE_MANUAL_DECISION_STATUS_MAP = new Map([
  ['approve', 'pass'],
  ['reject', 'fail'],
  ['request_changes', 'partial']
]);

export const get_manual_decision_status = (action) => SAFE_MANUAL_DECISION_STATUS_MAP.get(String(action || '').trim()) || 'pass';

export const format_detail_value = (value) => {
  if (value === undefined || value === null || value === '') return '-';
  if (typeof value !== 'object') return String(value);

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const is_verification_stage = (case_item = {}) => {
  const stage_values = [
    case_item?.current_workflow_status,
    case_item?.workflow_display_stage,
    case_item?.stage,
    case_item?.detail,
    case_item?.application_status,
    case_item?.status,
    case_item?.status_name
  ];

  return stage_values.some((value) => normalize_stage(value).includes('verif'));
};

export const is_manual_verification_stage = (case_item = {}) => {
  const stage_values = [
    case_item?.current_workflow_status,
    case_item?.workflow_display_stage,
    case_item?.stage,
    case_item?.detail,
    case_item?.application_status,
    case_item?.status,
    case_item?.status_name
  ];

  return stage_values.some((value) => {
    const normalized_value = normalize_stage(value);
    return normalized_value.includes('manual verif');
  });
};

const build_stage_verification_fallback_check = (case_item = {}) => ({
  id: `verification-stage-${case_item?.application_id || case_item?.id || Date.now()}`,
  check: 'Verification gate',
  case_name: case_item?.name || 'Onboarded Entity',
  source: 'Workflow gate',
  status: 'Pending',
  status_tone: 'warning',
  impact: 'Needs review',
  last_run: '-',
  application_form_id: null,
  service_id: null,
  verification_mode: 'auto',
  is_gate_placeholder: true,
  is_runnable: false,
  case_item
});

function build_stage_manual_verification_fallback_check(case_item = {}, binding = {}) {
  const verification_type = get_manual_stage_verification_type(binding);

  return build_stage_manual_verification_fallback_payload({
    id: get_manual_stage_fallback_id(case_item, binding),
    check: get_manual_stage_fallback_check_label(verification_type),
    case_name: get_case_row_name(case_item),
    verification_type,
    manual_binding: binding,
    case_item
  });
}

function build_stage_manual_verification_fallback_payload({
  id,
  check,
  case_name,
  verification_type,
  manual_binding,
  case_item
}) {
  return {
    id,
    check,
    case_name,
    source: 'Workflow gate',
    status: 'Pending',
    status_tone: 'warning',
    impact: 'Needs manual review',
    last_run: '-',
    application_form_id: null,
    service_id: null,
    verification_mode: 'manual',
    is_gate_placeholder: true,
    verification_type,
    manual_binding,
    run_id: null,
    case_item
  };
}

function get_manual_stage_fallback_id(case_item = {}, binding = {}) {
  const case_id = first_non_empty_value([case_item?.application_id, case_item?.id, Date.now()]);
  const binding_id = first_non_empty_value([binding?.id, binding?.verification_type, 'manual']);
  return `manual-verification-stage-${case_id}-${binding_id}`;
}

function get_manual_stage_verification_type(binding = {}) {
  return first_non_empty_value([binding?.verification_type, 'manual_verification']);
}

function get_manual_stage_fallback_check_label(verification_type) {
  return title_case(String(verification_type || 'Manual verification').replace(/[_-]+/g, ' '));
}

export const build_case_checks = ({
  case_with_stage,
  forms,
  verification_logs,
  verification_statuses_by_form,
  services,
  bound_services,
  manual_status_snapshot,
  manual_history,
  manual_mapping_by_link = {}
}) => {
  const automatic_checks = build_verification_checks({
    case_item: case_with_stage,
    forms,
    verification_logs,
    verification_statuses_by_form,
    services,
    bound_services,
    auto_status_snapshot: manual_status_snapshot,
    auto_history: manual_history
  });

  const manual_checks = build_manual_verification_checks({
    case_item: case_with_stage,
    manual_status_snapshot,
    manual_history,
    manual_bindings: case_with_stage.manual_verification_bindings,
    manual_mapping_by_link
  });

  const checks = [...automatic_checks, ...manual_checks];
  return checks.length
    ? checks
    : (is_manual_verification_stage(case_with_stage)
      ? (
        case_with_stage.manual_verification_bindings.length
          ? case_with_stage.manual_verification_bindings
            .map((binding) => build_stage_manual_verification_fallback_check(case_with_stage, binding))
          : [build_stage_manual_verification_fallback_check(case_with_stage)]
      )
      : (is_verification_stage(case_with_stage)
        ? [build_stage_verification_fallback_check(case_with_stage)]
        : []));
};
